import React, { useCallback, forwardRef } from 'react'
import { useSelector } from 'react-redux'

import Masthead, {
    MastheadMenuButton,
    MastheadTitle,
} from '@amzn/meridian/masthead'
import NavBarInfo from '@components/NavBar/NavBarInfo'
import NavBarProfile from '@components/NavBar/NavBarProfile'
import { usePlatformMode } from '@hooks/usePlatformMode'
import { isToolBarHidden } from '@atredux/selectors/toolSelector'
import { getStoreId as getStoreIdFromRedux } from '@atredux/selectors/storeSelector'
import { getNotificationSheetState } from '@atredux/selectors/notificationSelector'

import {
    navBarConfig,
} from '@utils/ui-config'
import { getStoreId } from '@src/utils/EnvironmentUtil'

import './NavBar.scss'

interface INavBarProps {
    toggleMenu: () => void
    showNewHomePageFeatures: boolean
    showMenuButton: boolean
    openExitSheet: () => void
    toggleProfile: () => void
    toggleProblemSolve: () => void
    toggleNotificationsSheet: () => void
    overrideToolName?: string
    isMenuOpen?: boolean
    isProfileOpen?: boolean
    /** Bottom nav in play, which makes the masthead menu button redundant. */
    isBottomNavMode?: boolean
}

/**
 * Renders the navigation bar for associate tools. This component
 * simply renders the various subcomponents that make up the navigation bar
 * and passes along the utility functions required as props.
 *
 * Usage:
 * <NavBar toggleMenu={toggleMenuFunction} />
 *
 * @param {Function} toggleMenu Function to toggle menu open/closed.
 * @param showMenuButton
 * @param toggleMenuButton
 * @returns {React.ReactElement}
 */
const NavBar = forwardRef<HTMLDivElement, INavBarProps>((
    {
        toggleMenu,
        showNewHomePageFeatures,
        showMenuButton,
        openExitSheet,
        toggleProfile,
        toggleProblemSolve,
        toggleNotificationsSheet,
        overrideToolName,
        isMenuOpen,
        isProfileOpen,
        isBottomNavMode,
    },
    ref,
): React.ReactElement => {
    const toggleMenuOnButtonClick = useCallback(() => toggleMenu(), [toggleMenu])

    const platformMode = usePlatformMode()
    const userEmail = localStorage.getItem('userEmail') || ''
    const isToolBarHiddenUI = useSelector(isToolBarHidden)
    const storeIdFromRedux = useSelector(getStoreIdFromRedux)
    const currentStoreId = storeIdFromRedux || getStoreId()
    const isNotificationsOpen = useSelector(getNotificationSheetState)

    const displayProperty = isToolBarHiddenUI ? 'none' : 'flex'

    return (
        <div
            ref={ref}
            className={`navbar-container ${isMenuOpen ? 'sidebar-open' : ''} ${isNotificationsOpen ? 'notifications-open' : ''} ${isProfileOpen ? 'profile-open' : ''}`}
            style={{ display: displayProperty }}
        >
            <Masthead
                data-atb-id={navBarConfig.MASTHEAD_ID}
                size={navBarConfig.NAVBAR_SIZE}
            >
                <MastheadTitle
                    data-atb-id={'navbar-masthead-title-button'}
                    onClick={toggleMenuOnButtonClick}
                >
                    <NavBarInfo
                        userEmail={userEmail}
                        storeId={currentStoreId}
                        showNewHomePageFeatures={showNewHomePageFeatures}
                        showMenuButton={showMenuButton}
                        openExitSheet={openExitSheet}
                        toggleProfile={toggleProfile}
                        toggleProblemSolve={toggleProblemSolve}
                        toggleNotificationsSheet={toggleNotificationsSheet}
                        overrideToolName={overrideToolName}
                        isShellPageOpen={Boolean(isMenuOpen || isNotificationsOpen || isProfileOpen)}
                    />
                </MastheadTitle>
                {/* Controlling the show/hide menu button toggle based on showNewHomePageFeatures is enabled or not.
                    Hidden in bottom nav mode: it only opened the tool list, which the
                    bottom nav's tools tab now owns. showMenuButton itself is left alone
                    because NavBarInfo reads it inversely to show the exit button. */}
                {(showMenuButton && !isBottomNavMode) && (
                    <MastheadMenuButton
                        data-atb-id={'navbar-masthead-menu-button'}
                        onClick={toggleMenuOnButtonClick}
                        open={true}
                    />
                )}
                {/* Cloudscape (Portal) mode: inline profile with name, email, and sign out. */}
                {platformMode === 'cloudscape' && (
                    <NavBarProfile />
                )}
            </Masthead>
        </div>
    )
})

export default NavBar
